import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  experienceStorage,
  STORAGE_KEYS,
} from '@experiences/blocs/utils/storage';
import { FulfillmentName } from '@/types';

/**
 * Selected Fulfillment Store for Sales Portal
 *
 * Manages user's fulfillment selections per restaurant with automatic persistence
 * to MMKV storage. Each restaurant can have one saved fulfillment selection that
 * includes the date, fulfillment type, settings, and optional timeslot.
 *
 * @persistence Automatically syncs to MMKV storage using zustand persist middleware
 * @storage_key STORAGE_KEYS.FULFILLMENT_MAP
 */

/**
 * Represents a saved fulfillment selection for a restaurant.
 * Contains all information needed to restore a user's fulfillment choice.
 */
export interface SavedSelectedFulfillment {
  /** Selected fulfillment date in ISO string format */
  date: string;
  /** UUID of the fulfillment settings, null if not applicable */
  fulfillmentSettingsUUID: string | null;
  /** Optional timeslot with start and end times */
  timeslot: { endTime: string; startTime: string } | null;
  /** Type of fulfillment (e.g., delivery, pickup) */
  type: FulfillmentName;
}

/**
 * Map of restaurant UUIDs to their saved fulfillment selections.
 * Allows storing one fulfillment selection per restaurant.
 */
export interface SelectedFulfillmentMap {
  [restaurantUUID: string]: SavedSelectedFulfillment;
}

/**
 * Store state containing persisted fulfillment selections.
 * This is the source of truth for all saved fulfillment data.
 */
interface SelectedFulfillmentState {
  /** Map of restaurant UUIDs to their saved fulfillment selections */
  savedSelectedFulfillments: SelectedFulfillmentMap;
}

/**
 * Store actions for managing fulfillment selections.
 * Provides methods to get and save fulfillment selections per restaurant.
 */
interface SelectedFulfillmentActions {
  /**
   * Gets the saved fulfillment selection for a specific restaurant.
   * @param restaurantUUID - The UUID of the restaurant
   * @returns The saved fulfillment or null if none exists
   */
  getSavedSelectedFulfillment: (
    restaurantUUID: string,
  ) => SavedSelectedFulfillment | null;
  /**
   * Saves a fulfillment selection for a specific restaurant.
   * Automatically persists to MMKV storage.
   * @param restaurantUUID - The UUID of the restaurant
   * @param fulfillment - The fulfillment selection to save
   */
  saveSelectedFulfillment: (
    restaurantUUID: string,
    fulfillment: SavedSelectedFulfillment,
  ) => void;
}

type SelectedFulfillmentStore = SelectedFulfillmentState &
  SelectedFulfillmentActions;

export const useSelectedFulfillmentStore = create<SelectedFulfillmentStore>()(
  persist(
    (set, get) => ({
      getSavedSelectedFulfillment: (restaurantUUID: string) => {
        const { savedSelectedFulfillments } = get();
        return savedSelectedFulfillments[restaurantUUID] || null;
      },

      savedSelectedFulfillments: {},

      saveSelectedFulfillment: (
        restaurantUUID: string,
        fulfillment: SavedSelectedFulfillment,
      ) => {
        set((state) => ({
          savedSelectedFulfillments: {
            ...state.savedSelectedFulfillments,
            [restaurantUUID]: fulfillment,
          },
        }));
      },
    }),
    {
      name: STORAGE_KEYS.FULFILLMENT_MAP,
      storage: createJSONStorage(() => ({
        getItem: (key) => {
          const value = experienceStorage.getString(key);
          return value || null;
        },
        removeItem: (key) => {
          experienceStorage.remove(key);
        },
        setItem: (key, value) => {
          experienceStorage.set(key, value);
        },
      })),
    },
  ),
);

/**
 * Selector to get the saved fulfillment selection for a specific restaurant
 * Use this with Zustands selector pattern to prevent unnecessary re-renders
 *
 * @param state - The selected fulfillment store state
 * @param restaurantUUID - The UUID of the restaurant to get the fulfillment for
 * @returns The saved fulfillment selection for the restaurant, or null if none exists
 *
 * @example
 * ```typescript
 * const fulfillment = useSelectedFulfillmentStore(
 *   (state) => selectRestaurantFulfillment(state, restaurantUUID)
 * );
 * ```
 */
export const selectRestaurantFulfillment = (
  state: SelectedFulfillmentState,
  restaurantUUID: string,
): SavedSelectedFulfillment | null => {
  return state.savedSelectedFulfillments[restaurantUUID] || null;
};
